import { z } from 'zod';
import { pageAccessLevelSchema } from './auth.js';
/** Covers reference uploaded images or theme tokens, never external URLs. */
export const pageCoverSchema = z.union([
    z.string().regex(/^file:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i),
    z.enum(['tone:selection', 'tone:info', 'tone:warning', 'tone:success']),
]);
const uuidSchema = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
/** An emoji, or `file:<id>` for an uploaded image. Never a data URL. */
const pageIconValueSchema = z.string().trim().min(1).max(64);
export const createPageSchema = z.object({
    title: z.string().trim().max(200).optional(),
    icon: pageIconValueSchema.optional(),
    parentId: uuidSchema.nullable().optional(),
});
/** 0 default, 1 serif, 2 mono. Matches the prototype's font cycle. */
export const pageFontSchema = z.union([z.literal(0), z.literal(1), z.literal(2)]);
export const pageAppearanceSchema = z.object({
    font: pageFontSchema,
    small: z.boolean(),
    wide: z.boolean(),
});
export const DEFAULT_PAGE_APPEARANCE = { font: 0, small: false, wide: false };
/**
 * Page font and width live under `properties.appearance`. `properties` is the
 * existing jsonb (reserved in the spec for database-row fields); a nested key
 * keeps appearance out of those fields without a new column.
 */
export function readPageAppearance(properties) {
    const raw = properties && typeof properties === 'object'
        ? properties['appearance']
        : undefined;
    const parsed = pageAppearanceSchema.partial().safeParse(raw ?? {});
    if (!parsed.success)
        return { ...DEFAULT_PAGE_APPEARANCE };
    return {
        font: parsed.data.font ?? DEFAULT_PAGE_APPEARANCE.font,
        small: parsed.data.small ?? DEFAULT_PAGE_APPEARANCE.small,
        wide: parsed.data.wide ?? DEFAULT_PAGE_APPEARANCE.wide,
    };
}
export const updatePageSchema = z
    .object({
    title: z.string().trim().max(200).optional(),
    icon: pageIconValueSchema.nullable().optional(),
    cover: pageCoverSchema.nullable().optional(),
    parentId: uuidSchema.nullable().optional(),
    /** Place this page immediately before this sibling. Omit to append. */
    beforeId: uuidSchema.nullable().optional(),
    isLocked: z.boolean().optional(),
    font: pageFontSchema.optional(),
    small: z.boolean().optional(),
    wide: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'empty' });
/** Toggle a personal favorite (productspec §5). */
export const setFavoriteSchema = z.object({ favorite: z.boolean() });
/**
 * ProseMirror document. The node tree is opaque to the API beyond "it is a
 * doc"; the editor owns the schema. Size is capped so a save cannot fill the
 * row.
 */
export const saveDocumentSchema = z.object({
    version: z.number().int().nonnegative(),
    content: z
        .object({
        type: z.literal('doc'),
        content: z.array(z.unknown()).max(5_000).optional(),
    })
        .refine((doc) => JSON.stringify(doc).length <= 400_000, { message: 'too_large' }),
});
export const inviteBodySchema = z.object({
    email: z.string().trim().email().max(255),
    role: z.enum(['admin', 'member', 'guest']),
});
export const searchQuerySchema = z.object({
    q: z.string().trim().min(1).max(200),
});
/**
 * The four switches on Settings → General. They live in `workspaces.settings`
 * (jsonb), so a toggle needs no migration.
 */
export const workspaceSettingsSchema = z.object({
    allowGuests: z.boolean(),
    membersInvite: z.boolean(),
    publish: z.boolean(),
    inheritAccess: z.boolean(),
});
/** What a workspace gets when its settings jsonb is still empty. */
export const DEFAULT_WORKSPACE_SETTINGS = {
    allowGuests: true,
    membersInvite: true,
    publish: false,
    inheritAccess: true,
};
export const updateWorkspaceSchema = z.object({
    name: z.string().trim().min(1).max(64).optional(),
    settings: workspaceSettingsSchema.partial().optional(),
});
/**
 * Who a share is for. `public` is the row that makes a page readable by anyone
 * with the link; `email` is an address that has no account yet.
 */
export const sharePrincipalSchema = z.object({
    type: z.enum(['user', 'email', 'public']),
    id: z.string().trim().max(255),
});
export const upsertShareSchema = z.object({
    principal: sharePrincipalSchema,
    level: pageAccessLevelSchema,
});
/** `invited` is no row at all: only explicit shares reach the page. */
export const generalAccessSchema = z.object({
    general: z.enum(['invited', 'workspace', 'web']),
});
/** Block paths are zero-based child indexes within one document version. */
const mcpBlockPathSchema = z
    .string()
    .regex(/^\d+(?:\.\d+)*$/)
    .max(200);
export const mcpPageStructureSchema = z
    .object({
    workspaceId: uuidSchema.optional(),
    pageId: uuidSchema,
})
    .strict();
/**
 * Every block the editor can show, in the shape an AI writes it.
 * `insert_blocks` turns these into editor JSON; the block vocabulary tool
 * documents them, and a test keeps the two in sync.
 */
export const MCP_BLOCK_TYPES = [
    'paragraph',
    'heading',
    'bulletList',
    'orderedList',
    'taskList',
    'blockquote',
    'codeBlock',
    'horizontalRule',
    'tableOfContents',
    'table',
    'toggle',
    'callout',
    'database',
    'databaseLink',
    'media',
    'childPage',
];
const mcpTextSchema = z.string().max(10_000);
const mcpBlockAppearance = {
    color: z
        .enum(['gray', 'brown', 'orange', 'yellow', 'green', 'blue', 'purple', 'pink', 'red'])
        .optional(),
    bg: z.enum(['gray', 'yellow', 'green', 'blue', 'pink', 'red']).optional(),
    row: z.string().min(1).max(64).optional(),
    cw: z.number().min(0.1).max(10).optional(),
};
export const mcpBlockSchema = z.discriminatedUnion('type', [
    z
        .object({ type: z.literal('paragraph'), text: mcpTextSchema })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({
        type: z.literal('heading'),
        level: z.number().int().min(1).max(4),
        text: mcpTextSchema,
    })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({
        type: z.literal('bulletList'),
        items: z
            .array(z.union([
            mcpTextSchema,
            z
                .object({
                text: mcpTextSchema,
                get children() {
                    return z.array(mcpBlockSchema).max(50).optional();
                },
            })
                .strict(),
        ]))
            .min(1)
            .max(200),
    })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({
        type: z.literal('orderedList'),
        items: z
            .array(z.union([
            mcpTextSchema,
            z
                .object({
                text: mcpTextSchema,
                get children() {
                    return z.array(mcpBlockSchema).max(50).optional();
                },
            })
                .strict(),
        ]))
            .min(1)
            .max(200),
    })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({
        type: z.literal('taskList'),
        items: z
            .array(z
            .object({ text: mcpTextSchema, checked: z.boolean().default(false) })
            .extend(mcpBlockAppearance)
            .strict())
            .min(1)
            .max(200),
    })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({ type: z.literal('blockquote'), text: mcpTextSchema })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({
        type: z.literal('codeBlock'),
        text: z.string().max(100_000),
        language: z.string().trim().max(32).optional(),
    })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({ type: z.literal('horizontalRule') })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({ type: z.literal('tableOfContents') })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({
        type: z.literal('table'),
        rows: z
            .array(z.array(z.string().max(2_000)).min(1).max(24))
            .min(1)
            .max(100),
    })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({
        type: z.literal('toggle'),
        level: z.number().int().min(0).max(4).default(0),
        open: z.boolean().default(true),
        title: z.string().max(2_000),
        body: z.string().max(100_000).optional(),
        get children() {
            return z.array(mcpBlockSchema).max(50).optional();
        },
    })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({
        type: z.literal('callout'),
        icon: z.string().trim().max(32).optional(),
        body: z.string().trim().min(1).max(100_000).optional(),
        get children() {
            return z.array(mcpBlockSchema).max(50).optional();
        },
    })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({ type: z.literal('databaseLink'), dataSourceId: uuidSchema })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({
        type: z.literal('database'),
        dataSourceId: uuidSchema,
        viewId: z.string().min(1).max(64).optional(),
    })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({
        type: z.literal('media'),
        fileId: uuidSchema,
        mode: z.enum(['link', 'card', 'embed']).optional(),
        width: z.number().int().min(20).max(100).optional(),
    })
        .extend(mcpBlockAppearance)
        .strict(),
    z
        .object({ type: z.literal('childPage'), pageId: uuidSchema })
        .extend(mcpBlockAppearance)
        .strict(),
]);
export const mcpPageEditSchema = mcpPageStructureSchema
    .extend({
    version: z.number().int().nonnegative(),
    operation: z.discriminatedUnion('type', [
        z
            .object({ type: z.literal('append'), markdown: z.string().trim().min(1).max(100_000) })
            .strict(),
        z
            .object({
            type: z.literal('insert_blocks'),
            after: mcpBlockPathSchema.optional(),
            blocks: z.array(mcpBlockSchema).min(1).max(50),
        })
            .strict(),
        z
            .object({
            type: z.literal('insert_after_heading'),
            path: mcpBlockPathSchema,
            markdown: z.string().trim().min(1).max(100_000),
        })
            .strict(),
        z
            .object({
            type: z.literal('replace_text'),
            path: mcpBlockPathSchema,
            find: z.string().min(1).max(10_000),
            replacement: z.string().max(10_000),
        })
            .strict(),
        z
            .object({
            type: z.literal('delete_blocks'),
            from: mcpBlockPathSchema,
            to: mcpBlockPathSchema,
        })
            .strict(),
    ]),
})
    .strict();
/** Split escaped pipes without treating cell text as Markdown formatting. */
function markdownCells(line) {
    const cells = [];
    let cell = '';
    const body = line
        .trim()
        .replace(/^\|/, '')
        .replace(/(?<!\\)\|$/, '');
    for (let index = 0; index < body.length; index += 1) {
        const character = body[index] ?? '';
        if (character === '\\' && ['\\', '|'].includes(body[index + 1] ?? '')) {
            cell += body[index + 1];
            index += 1;
        }
        else if (character === '|') {
            cells.push(cell.trim());
            cell = '';
        }
        else
            cell += character;
    }
    cells.push(cell.trim());
    return cells;
}
export function parseMarkdownTable(lines, start) {
    const header = lines[start] ?? '';
    const separator = markdownCells(lines[start + 1] ?? '');
    if (!header.trim().startsWith('|') || !separator.every((cell) => /^:?-{3,}:?$/.test(cell)))
        return null;
    const first = markdownCells(header);
    if (first.length !== separator.length || first.length > 24)
        return null;
    const rows = [first];
    let index = start + 2;
    while ((lines[index] ?? '').trim().startsWith('|')) {
        const cells = markdownCells(lines[index] ?? '');
        if (cells.length !== first.length || rows.length >= 100)
            break;
        rows.push(cells);
        index += 1;
    }
    return { rows, lineCount: index - start };
}
export function formatMarkdownTable(rows) {
    const escape = (text) => text.replace(/\\/g, '\\\\').replace(/\|/g, '\\|').replace(/\r?\n/g, ' ');
    const line = (cells) => `| ${cells.map(escape).join(' | ')} |`;
    const [header, ...body] = rows;
    if (!header)
        return '';
    return [line(header), line(header.map(() => '---')), ...body.map(line)].join('\n');
}
export const TABLE_OF_CONTENTS_MARKER = '[TOC]';
/** An extra backslash preserves literal marker text on a Markdown roundtrip. */
export function escapeContentsLine(text) {
    return text.replace(/^([ \t]*)(\\*)\[TOC\]([ \t]*)$/gm, '$1\\$2[TOC]$3');
}
export function parseContentsLine(line) {
    const match = /^([ \t]*)(\\*)\[TOC\]([ \t]*)$/.exec(line);
    if (!match)
        return null;
    const slashes = match[2] ?? '';
    return slashes.length === 0
        ? { kind: 'contents' }
        : {
            kind: 'text',
            text: `${match[1] ?? ''}${slashes.slice(1)}${TABLE_OF_CONTENTS_MARKER}${match[3] ?? ''}`,
        };
}
//# sourceMappingURL=pages.js.map